import { useEffect, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import { classList, sectionList } from "../../utils";
import { toast } from "react-toastify";
import { getSession } from "../../functions";
import DownloadResult from "../../components/DownloadResult";
import DownloadAnnualResult from "../../components/DownloadAnnualResult";
import DownloadResultXLS from "../../components/result/DownloadResultXLS";
import DownloadAnnualResultXLS from "../../components/result/DownloadAnnualResultXLS";

// ── Session config (change once per year) ─────────────────────────────────────
const SESSION_START_YEAR = 2026; // 2026-27 session
const SESSION_LABEL = `${SESSION_START_YEAR}-${SESSION_START_YEAR + 1}`; // "2026-2027"

const HALF_YEARLY_FROM = new Date(SESSION_START_YEAR, 3, 1); // 1 Apr 2026
const HALF_YEARLY_TO = new Date(SESSION_START_YEAR, 8, 30); // 30 Sep 2026
const ANNUAL_FROM = new Date(SESSION_START_YEAR, 3, 1); // 1 Apr 2026
const ANNUAL_TO = new Date(SESSION_START_YEAR + 1, 2, 31); // 31 Mar 2027

// ── Defined OUTSIDE Result to prevent remount on every state change ───────────

const ClassSectionPicker = ({
  selectedClass,
  selectedSection,
  setSelectedClass,
  setSelectedSection,
  onDownload,
  isDownloading,
}) => (
  <>
    <div className="mt-4">
      <div className="bg-light rounded-pill py-2 mb-3">
        <h5 className="fs-5 text-center m-0 lh-1 fw-medium">Select Class</h5>
      </div>
      <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
        {classList.map((cls, index) => (
          <div
            key={index}
            className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
              selectedClass === cls
                ? "bg-warning text-white border-warning"
                : ""
            }`}
            onClick={() => setSelectedClass(cls)}
          >
            {cls}
          </div>
        ))}
      </div>
    </div>

    <div className="mt-4">
      <div className="bg-light rounded-pill py-2 mb-3">
        <h5 className="fs-5 text-center m-0 lh-1 fw-medium">Select Section</h5>
      </div>
      <div className="d-flex flex-wrap justify-content-center gap-1 px-lg-8 px-5 mx-lg-4">
        {sectionList.map((sec, index) => (
          <div
            key={index}
            className={`bg-dark-soft rounded-pill cursor-pointer px-2 py-1 fs-6 ${
              selectedSection === sec
                ? "bg-warning text-white border-warning"
                : ""
            }`}
            onClick={() => setSelectedSection(sec)}
          >
            <span className="mx-1">{sec}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
      <div>
        {isDownloading ? (
          <div className="bg-primary md-circle-button fw-medium">
            <div
              className="spinner-border spinner-border-md text-white"
              role="status"
            ></div>
          </div>
        ) : (
          <button
            onClick={onDownload}
            className="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
          >
            Download
          </button>
        )}
      </div>
    </div>
  </>
);

const ResultModal = ({
  id,
  title,
  subtitle,
  onDownload,
  isDownloading,
  selectedClass,
  selectedSection,
  setSelectedClass,
  setSelectedSection,
}) => (
  <div className="modal m-0 fade" id={id} tabIndex="-1" aria-hidden="true">
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content rounded-5 overflow-hidden">
        <div className="p-4 bg-danger-soft d-flex justify-content-end">
          <i
            className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></i>
        </div>
        <div className="pt-0 p-5">
          <div className="d-flex justify-content-center mt-n8">
            <img
              src="/assets/images/report.jpg"
              width="90"
              className="object-fit-contain p-3 rounded-5 bg-white"
            />
          </div>
          <div className="mt-2">
            <h5 className="mb-1 text-center">{title}</h5>
            <h5 className="text-center lh-1 fw-medium">
              Class / Section Wise
              <br />
              <span className="text-secondary">{subtitle}</span>
            </h5>
          </div>
          <ClassSectionPicker
            selectedClass={selectedClass}
            selectedSection={selectedSection}
            setSelectedClass={setSelectedClass}
            setSelectedSection={setSelectedSection}
            onDownload={onDownload}
            isDownloading={isDownloading}
          />
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

function Result() {
  const [complainBoxDetails, setComplainBoxDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [selectedClass, setSelectedClass] = useState("Uday");
  const [selectedSection, setSelectedSection] = useState("B");
  const [isDownloading, setIsDownloading] = useState(false);

  // ── Shared data fetcher ───────────────────────────────────────────────
  // Working days & holidays use the SAME logic as the Attendance Register:
  // every date in the range, minus this class's holiday dates (each date counted once).
  const fetchResultData = async (examFromDate, examToDate) => {
    const marksRes = await fetch(
      `${server}/GetStudentMarksThroughFilter?session=${SESSION_LABEL}&examType=half%20yearly&class=${selectedClass}&section=${selectedSection}`,
      { headers: { Authorization: `${localStorage.getItem("token")}` } },
    );
    const marksData = await marksRes.json();

    const headsRes = await fetch(
      `${server}/GetResultHeadsOfParticularSessionAndClass?session=${SESSION_LABEL}&class=${selectedClass}`,
      { headers: { Authorization: `${localStorage.getItem("token")}` } },
    );
    const headsData = await headsRes.json();

    const attendanceRes = await fetch(`${server}/search_attendance_mongodb`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        Class: selectedClass,
        Section: selectedSection,
        "From Date": examFromDate.toLocaleDateString("en-GB"),
        "To Date": examToDate.toLocaleDateString("en-GB"),
      }),
    });
    const attendanceJson = await attendanceRes.json();
    const attendanceRaw = (attendanceJson?.["body-json"]?.body || []).map(
      (s, i) => ({
        ...s,
        rollNo: s.rollNo || i + 1,
        name: s.name,
      }),
    );

    // Same session source as the Attendance Register
    const holidayRes = await fetch(
      `${server}/GetAllDefaultHolidays?session=${getSession()}`,
      {
        method: "GET",
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    const holidayJson = await holidayRes.json();

    // "YYYY-MM-DD" -> "DD/MM/YYYY" (same as Attendance Register)
    const holidays = (holidayJson?.holidays || []).map((h) => {
      const [year, month, day] = h.date.split("-");
      return { ...h, date: `${day}/${month}/${year}` };
    });

    // Unique holiday dates for the selected class
    const classHolidayDates = new Set(
      holidays
        .filter((h) => h.class?.includes(selectedClass))
        .map((h) => h.date),
    );

    // Count each date in range once; skip holiday dates
    let totalDays = 0;
    let workingDays = 0;
    const cursor = new Date(examFromDate);
    while (cursor <= examToDate) {
      totalDays++;
      if (!classHolidayDates.has(cursor.toLocaleDateString("en-GB")))
        workingDays++;
      cursor.setDate(cursor.getDate() + 1);
    }

    // Remove holiday dates from attendance so Present count matches the register
    const attendanceData = attendanceRaw.map((s) => ({
      ...s,
      attendance: (s.attendance || []).filter(
        (d) => !classHolidayDates.has(d.Date),
      ),
    }));

    return {
      marksData,
      headsData,
      attendanceData,
      holidays,
      totalDays,
      workingDays,
    };
  };

  // ── Half Yearly PDF ───────────────────────────────────────────────────
  const handleDownloadClassSectionWiseResult = async () => {
    if (!selectedClass || !selectedSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);

      const {
        marksData,
        headsData,
        attendanceData,
        holidays,
        totalDays,
        workingDays,
      } = await fetchResultData(HALF_YEARLY_FROM, HALF_YEARLY_TO);

      await DownloadResult({
        marks: marksData,
        heads: headsData,
        attendanceData,
        holidayData: holidays,
        selectedClass,
        selectedSection,
        totalDays,
        workingDays,
      });

      setSelectedClass("");
      setSelectedSection("");
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Half Yearly XLS ───────────────────────────────────────────────────
  const handleDownloadClassSectionWiseResultXLS = async () => {
    if (!selectedClass || !selectedSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);

      const { marksData, headsData, attendanceData, workingDays } =
        await fetchResultData(HALF_YEARLY_FROM, HALF_YEARLY_TO);

      await DownloadResultXLS({
        marks: marksData,
        heads: headsData,
        attendanceData,
        selectedClass,
        selectedSection,
        workingDays,
      });

      setSelectedClass("");
      setSelectedSection("");
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Annual PDF ────────────────────────────────────────────────────────
  const handleDownloadClassSectionWiseAnnualResult = async () => {
    if (!selectedClass || !selectedSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);

      const { marksData, headsData, attendanceData, holidays, workingDays } =
        await fetchResultData(ANNUAL_FROM, ANNUAL_TO);

      await DownloadAnnualResult({
        marks: marksData,
        heads: headsData,
        attendanceData,
        selectedClass,
        selectedSection,
        workingDays,
        holidays,
      });

      setSelectedClass("");
      setSelectedSection("");
    } catch (error) {
      console.error(error);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Annual XLS ────────────────────────────────────────────────────────
  const handleDownloadClassSectionWiseAnnualResultXLS = async () => {
    if (!selectedClass || !selectedSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);

      const { marksData, headsData, attendanceData, holidays, workingDays } =
        await fetchResultData(ANNUAL_FROM, ANNUAL_TO);

      await DownloadAnnualResultXLS({
        marks: marksData,
        heads: headsData,
        attendanceData,
        selectedClass,
        selectedSection,
        workingDays,
        holidays,
      });

      setSelectedClass("");
      setSelectedSection("");
    } catch (error) {
      console.error(error);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  const fetchAdmitCardDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${server}/GetAllAdmitCards`, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      });
      const responseData = response.data;
      if (Array.isArray(responseData)) {
        setComplainBoxDetails(responseData);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmitCardDetails();
  }, []);

  const filteredRecords = complainBoxDetails.filter((ComplainBox) => {
    const searchLower = searchTerm.toLowerCase();
    return Object.values(ComplainBox || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Shared props passed down to every modal
  const modalSharedProps = {
    isDownloading,
    selectedClass,
    selectedSection,
    setSelectedClass,
    setSelectedSection,
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table className="table mb-0 text-nowrap table-centered">
                    <thead className="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody></tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="">
                  <div className="mb-3">
                    <div className="bg-white rounded-pill">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol className="breadcrumb pe-3">
                          <li className="breadcrumb-item">
                            <a href="#">Examinations</a>
                          </li>
                          <li
                            className="breadcrumb-item active"
                            aria-current="page"
                          >
                            Result
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}
                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Result
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            0.2 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div className="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div className="rounded-4 d-flex flex-wrap">
                              <span className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                All Examination Report
                              </span>
                              <span className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Term Wise
                              </span>
                              <span className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Examination Class Wise Report
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>

                    <div className="mt-1">
                      <div className="row g-1">
                        <div className="col-6">
                          <div
                            data-bs-toggle="modal"
                            data-bs-target="#admitCardClassSectionWiseModal"
                            className="p-3 bg-success-soft rounded-4 cursor-pointer h-100"
                          >
                            <p className="m-0 text-center fw-medium lh-sm text-dark">
                              Half Yearly Examination Result (PDF)
                            </p>
                            <p className="text-center fs-5 m-0 lh-1 mt-2">
                              Class / Section Wise
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div
                            data-bs-toggle="modal"
                            data-bs-target="#halfYearlyXLSModal"
                            className="p-3 bg-success-soft rounded-4 cursor-pointer h-100"
                          >
                            <p className="m-0 text-center fw-medium lh-sm text-dark">
                              Half Yearly Examination Result (XLS)
                            </p>
                            <p className="text-center fs-5 m-0 lh-1 mt-2">
                              Class / Section Wise
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div
                            data-bs-toggle="modal"
                            data-bs-target="#handleDownloadClassSectionWiseAnnualResultModal"
                            className="p-3 bg-danger-soft rounded-4 cursor-pointer h-100"
                          >
                            <p className="m-0 text-center fw-medium lh-sm text-dark">
                              Annual Examination Result (PDF)
                            </p>
                            <p className="text-center fs-5 m-0 lh-1 mt-2">
                              Class / Section Wise
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div
                            data-bs-toggle="modal"
                            data-bs-target="#annualXLSModal"
                            className="p-3 bg-danger-soft rounded-4 cursor-pointer h-100"
                          >
                            <p className="m-0 text-center fw-medium lh-sm text-dark">
                              Annual Examination Result (XLS)
                            </p>
                            <p className="text-center fs-5 m-0 lh-1 mt-2">
                              Class / Section Wise
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);
                          if (currentPage > 3)
                            pageNumbers.push("left-ellipsis");
                          if (currentPage !== 1 && currentPage !== totalPages)
                            pageNumbers.push(currentPage);
                          if (currentPage < totalPages - 2)
                            pageNumbers.push("right-ellipsis");
                          if (totalPages > 1) pageNumbers.push(totalPages);

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }
                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}
                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>

                {/* ── Half Yearly PDF Modal ── */}
                <ResultModal
                  id="admitCardClassSectionWiseModal"
                  title="Half Yearly Examination Result"
                  subtitle="Generate PDF Report Card"
                  onDownload={handleDownloadClassSectionWiseResult}
                  {...modalSharedProps}
                />

                {/* ── Half Yearly XLS Modal ── */}
                <ResultModal
                  id="halfYearlyXLSModal"
                  title="Half Yearly Examination Result"
                  subtitle="Generate Excel Sheet"
                  onDownload={handleDownloadClassSectionWiseResultXLS}
                  {...modalSharedProps}
                />

                {/* ── Annual PDF Modal ── */}
                <ResultModal
                  id="handleDownloadClassSectionWiseAnnualResultModal"
                  title="Annual Examination Result"
                  subtitle="Generate PDF Report Card"
                  onDownload={handleDownloadClassSectionWiseAnnualResult}
                  {...modalSharedProps}
                />

                {/* ── Annual XLS Modal ── */}
                <ResultModal
                  id="annualXLSModal"
                  title="Annual Examination Result"
                  subtitle="Generate Excel Sheet"
                  onDownload={handleDownloadClassSectionWiseAnnualResultXLS}
                  {...modalSharedProps}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default Result;
